import { capitalize } from "src/app/core/v2/utils"
import { VAssetClassConsiderationRemote, VAssetClassSourceRemote, VLandAssetClassConsiderationRemote } from "../api/consideration"
import { VAssetClassConsideration, VAssetClassConsiderationLand, VAssetClassConsiderationRentEffective, VAssetClassConsiderationRentHeadline, VAssetClassConsiderationSale, VAssetClassSource } from "../common/consideration"

function _valueCalc(remote: VAssetClassConsiderationRemote): number {
    if (!remote.old_tenure) return remote.value
    if (remote.old_tenure.rent_input_amount_type === 'total') {
        return remote.value
    }
    if (remote.old_tenure.rent_input_amount_type === 'per_units') {
        return remote.old_tenure.total_consideration * (remote.old_tenure.rent_size ? remote.old_tenure.rent_size.size : 0)
    }
    return remote.value
}

function mapConsiderationRemoteToConsiderationSale(considerationRemote: VAssetClassConsiderationRemote): VAssetClassConsiderationSale {
  return {
    id: considerationRemote.id,
    tenureName: capitalize(considerationRemote.tenure),
    considerationType: 'Sale',
    conversion: considerationRemote.conversion.map(conv => ({ id: conv.id, quoteCurrency: conv.quote_currency, exchangeRate: conv.exchange_rate })),
    currency: considerationRemote.currency,
    oldTenure: considerationRemote.old_tenure,
    source: mapSourceRemoteToDomain(considerationRemote.source),
    value: _valueCalc(considerationRemote)
  }
}

function mapConsiderationRemoteToConsiderationRent(considerationRemote: VAssetClassConsiderationRemote): VAssetClassConsiderationRentEffective | VAssetClassConsiderationRentHeadline {
  if (considerationRemote.rent_type === 'headline') {
    return {
      id: considerationRemote.id,
      tenureName: capitalize(considerationRemote.tenure),
      considerationType: 'Rent',
      rentType: 'Headline',
      conversion: considerationRemote.conversion.map(conv => ({id: conv.id, quoteCurrency: conv.quote_currency, exchangeRate: conv.exchange_rate })),
      currency: considerationRemote.currency,
      oldTenure: considerationRemote.old_tenure,
      source: mapSourceRemoteToDomain(considerationRemote.source),
      value: _valueCalc(considerationRemote)
    }
  }
  return {
    id: considerationRemote.id,
    tenureName: capitalize(considerationRemote.tenure),
    considerationType: 'Rent',
    rentType: 'Effective',
    conversion: considerationRemote.conversion.map(conv => ({id: conv.id, quoteCurrency: conv.quote_currency, exchangeRate: conv.exchange_rate })),
    currency: considerationRemote.currency,
    oldTenure: considerationRemote.old_tenure,
    source: mapSourceRemoteToDomain(considerationRemote.source),
    value: _valueCalc(considerationRemote)
  }
}

export function mapConsiderationRemoteToDomain(considerationsRemote: VAssetClassConsiderationRemote[]): VAssetClassConsideration[] {
  const considerations = considerationsRemote
    .map(con => {
      if (con.consideration_type_id === 1) {
        return mapConsiderationRemoteToConsiderationSale(con)
      } else if (con.consideration_type_id === 2) {
        return mapConsiderationRemoteToConsiderationRent(con)
      }
      return null
    })
    .filter(item => item !== null)

  considerations.sort((a, b) => {
    if (a.source == null || b.source == null) {
      return -1
    }
    const aDate = a.source.sourceType == 'Advertised' ? a.source.informationDate : a.source.transactionDate
    const bDate = b.source.sourceType == 'Advertised' ? b.source.informationDate : b.source.transactionDate
    return bDate.getTime() - aDate.getTime()
  })

  return considerations
}

export function mapSourceRemoteToDomain(source: VAssetClassSourceRemote): VAssetClassSource {
  if (source == null) {
    return null
  }
  const base = {
    sourceTypeName: source.source_type_name,
    sourceInformation: source.source_information_name,
    sourceCredibility: source.source_credibility,
    validationSource: !source.validation_source || !source.validation_source.length ? null : source.validation_source
  }
  switch (source.source_type_id) {
    case 1: {
      return {
        ...base,
        sourceType: 'Transacted',
        transactionDate: new Date(source.transaction_date),
        sourceDate: new Date(source.transaction_date),
        webAddress: source.web_address,
        picture_1: source.picture_1,
        picture_2: source.picture_2
      }
    }
    case 2: {
      return {
        ...base,
        sourceType: 'Advertised',
        informationDate: new Date(source.information_date),
        sourceDate: new Date(source.information_date),
        webAddress: source.web_address,
        picture_1: source.picture_1,
        picture_2: source.picture_2
      }
    }
    default:
      return null
  }
}

export function mapVLandConsiderationRemoteToDomain(remotes: VLandAssetClassConsiderationRemote[]): VAssetClassConsiderationLand[] {
  const considerations: VAssetClassConsiderationLand[] = remotes.map(remote => {
    return {
      id: remote.id,
      tenureName: remote.land_tenure_name,
      currency: remote.currency,
      value: remote.consideration,
      considerationType: 'Land',
      conversion: remote.conversion.map(con => ({id: con.id, quoteCurrency: con.quote_currency, exchangeRate: con.exchange_rate })),
      source: mapSourceRemoteToDomain(remote.source)
    } as const
  })
  considerations.sort((a, b) => {
    if (a.source == null || b.source == null) {
      return -1
    }
    const aDate = a.source.sourceType == 'Advertised' ? a.source.informationDate : a.source.transactionDate
    const bDate = b.source.sourceType == 'Advertised' ? b.source.informationDate : b.source.transactionDate
    return bDate.getTime() - aDate.getTime()
  })
  return considerations
}